import { FC, FormEvent, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { z } from "zod";
import { useResetPassword } from "../../queries";

const passwordSchema = z.object({
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(1, "Field is required"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type PasswordFormData = z.infer<typeof passwordSchema>;

interface SetPasswordFormProps {
  email: string;
  code: string;
}

export const SetPasswordForm: FC<SetPasswordFormProps> = ({ email, code }) => {
  const { resetPassword, isResetPasswordLoading } = useResetPassword();

  const [passwordFormData, setPasswordFormData] = useState<PasswordFormData>({
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordTouched, setPasswordTouched] = useState({
    newPassword: false,
    confirmPassword: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validatePassword = () => {
    const res = passwordSchema.safeParse(passwordFormData);
    if (res.success) {
      return undefined;
    }
    return res.error.format();
  };

  const passwordErrors = validatePassword();
  const isPasswordFormValid = Object.values(passwordFormData).every(v => !!v) && !passwordErrors;

  const handleSetPassword = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    resetPassword({
      email,
      code,
      newPassword: passwordFormData.newPassword,
    });
  };

  return (
    <Box component="form" width={"100%"} onSubmit={handleSetPassword}>
      <Stack justifyContent={"flex-start"} width={"100%"}>
        <Typography variant="h4" mt={4}>
          Reset Password
        </Typography>
        <Typography variant="body2" sx={{ mb: 4 }}>
          Enter your new password
        </Typography>
      </Stack>

      <TextField
        fullWidth
        variant="filled"
        autoComplete="new-password"
        sx={{ mb: 2 }}
        required
        type={showPassword ? "text" : "password"}
        placeholder="New Password"
        label={
          passwordTouched.newPassword && passwordErrors?.newPassword
            ? passwordErrors?.newPassword?._errors[0]
            : "New Password"
        }
        onBlur={() => setPasswordTouched(prev => ({ ...prev, newPassword: true }))}
        error={passwordTouched.newPassword ? !!passwordErrors?.newPassword?._errors.length : false}
        value={passwordFormData.newPassword}
        onChange={(e) => {
          setPasswordFormData((prev) => ({
            ...prev,
            newPassword: e.target.value,
          }));
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword((prev) => !prev)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        variant="filled"
        autoComplete="new-password"
        sx={{ mb: 3 }}
        required
        type={showConfirmPassword ? "text" : "password"}
        placeholder="Confirm Password"
        label={
          passwordTouched.confirmPassword && passwordErrors?.confirmPassword
            ? passwordErrors?.confirmPassword?._errors[0]
            : "Confirm Password"
        }
        onBlur={() => setPasswordTouched(prev => ({ ...prev, confirmPassword: true }))}
        error={passwordTouched.confirmPassword ? !!passwordErrors?.confirmPassword?._errors.length : false}
        value={passwordFormData.confirmPassword}
        onChange={(e) => {
          setPasswordFormData((prev) => ({
            ...prev,
            confirmPassword: e.target.value,
          }));
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        fullWidth
        color="secondary"
        variant="contained"
        type="submit"
        loading={isResetPasswordLoading}
        disabled={isResetPasswordLoading || !isPasswordFormValid}
      >
        Reset Password
      </Button>
    </Box>
  );
};
